import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/api/hooks/useCurrentUser';
import { useAcceptInvitation } from '@/api/hooks/useAcceptInvitation';
import { useDeclineInvitation, useReportInvitation } from '@/api/hooks/useInvitationPreview';
import { InvitationScopeCard, type InvitationScopeStatus } from './InvitationScopeCard';
import { usePublicInvitationPreview } from './usePublicInvitationPreview';

export function invitationTokenFromHash(hash: string): string | null {
  if (!hash.startsWith('#')) return null;
  const token = new URLSearchParams(hash.slice(1)).get('token');
  return token && token.trim() ? token : null;
}

function maskEmail(email: string | null | undefined): string {
  if (!email || !email.includes('@')) return 'another account';
  const [local, domain] = email.split('@', 2);
  return `${local?.[0] ?? '•'}•••@${domain}`;
}

function errorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('response' in error)) return null;
  const response = (error as { response?: { data?: { error?: { code?: string } } } }).response;
  return response?.data?.error?.code ?? null;
}

export function InvitationAcceptPage() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [token] = useState(() => {
    const value = invitationTokenFromHash(typeof window === 'undefined' ? '' : window.location.hash);
    return value;
  });
  useEffect(() => {
    if (token && typeof window !== 'undefined' && window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [token]);
  const preview = usePublicInvitationPreview(token);
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();
  const report = useReportInvitation();
  const acceptError = accept.error;

  const status = useMemo<InvitationScopeStatus | null>(() => {
    if (!preview.data) return null;
    if (errorCode(acceptError) === 'identity_mismatch') return 'mismatched';
    return preview.data.status;
  }, [acceptError, preview.data]);

  if (!token) {
    return <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8"><p role="alert" className="rounded-lg border p-4 text-sm">This invitation link is missing its token.</p></main>;
  }
  if (preview.isPending) {
    return <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8"><div role="status" className="w-full max-w-xl rounded-xl border p-8 text-sm text-muted-foreground">Loading invitation details…</div></main>;
  }
  if (preview.isError) {
    return <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8"><div role="alert" className="w-full max-w-xl rounded-xl border border-degraded-border bg-degraded-surface p-6 text-sm"><p>Couldn't load this invitation.</p><button type="button" onClick={() => preview.refetch()} className="mt-4 min-h-11 rounded-md bg-primary px-4 font-semibold text-primary-foreground">Retry</button></div></main>;
  }
  if (!preview.data?.invitation || !status) {
    return <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8"><p role="alert" className="rounded-lg border p-4 text-sm">This invitation is no longer available.</p></main>;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground">
      <InvitationScopeCard
        invitation={preview.data.invitation}
        status={status}
        currentEmailMasked={maskEmail(currentUser?.email)}
        onAccept={async () => {
          try {
            const result = await accept.mutateAsync({ token });
            navigate(result.destination, { replace: true });
          } catch { /* React Query exposes the mutation error for the status copy. */ }
        }}
        onDecline={() => { void decline.mutateAsync({ token }); }}
        onReport={() => { void report.mutateAsync({ token, reason: 'unexpected' }); }}
        onSwitchAccount={() => navigate('/login', { replace: false })}
        onRequestNew={() => undefined}
      />
    </main>
  );
}

export default InvitationAcceptPage;
